import { forwardRef, useCallback, useRef } from 'react';
import { IconFileUpload } from '@rossum/ui/icons/tabler';
import { Button, Stack, SvgIcon } from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';

interface FileUploadComponentProps {
  onChange: (value: string) => void;
  value?: string;
}

export const FileUploadComponent = forwardRef<
  HTMLInputElement,
  FileUploadComponentProps
>(({ onChange, value }, ref) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }

      const reader = new FileReader();
      reader.onload = e => {
        const base64 = e.target?.result as string;
        onChange(base64);
      };
      reader.readAsDataURL(file);
    },
    [onChange]
  );

  const handleClick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleClear = useCallback(() => {
    onChange('');
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }, [onChange]);

  return (
    <Stack
      direction="column"
      spacing={2}
      p={2}
      sx={{
        color: theme => theme.palette.text.secondary,
        border: '1px dashed',
        borderRadius: borderRadius('m'),
        alignItems: 'center',
      }}
    >
      <input
        type="file"
        ref={instance => {
          if (typeof ref === 'function') {
            ref(instance);
          }
          inputRef.current = instance;
        }}
        onChange={handleFileChange}
        style={{ display: 'none' }}
        accept="image/svg+xml, image/png"
      />
      {value ? (
        <img
          src={value}
          alt="Uploaded logo"
          style={{ maxWidth: '200px', maxHeight: '100px' }}
        />
      ) : (
        <SvgIcon fontSize="large">
          <IconFileUpload />
        </SvgIcon>
      )}
      {value ? (
        <Button
          variant="text"
          color="inherit"
          size="small"
          sx={{ alignSelf: 'center' }}
          onClick={handleClear}
        >
          Clear
        </Button>
      ) : (
        <Button
          variant="text"
          color="inherit"
          size="small"
          sx={{ alignSelf: 'center' }}
          onClick={handleClick}
        >
          Upload file
        </Button>
      )}
    </Stack>
  );
});
